import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CreditPackages } from "@/components/CreditPackages";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ACTION_LABELS } from "@/data/credits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Loader2, ArrowUpRight, ArrowDownRight, History, Gift, Copy } from "lucide-react";

export default function Credits() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/credits/history");
      setHistory(data);
    } catch {}
    setBusy(false);
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <div className="max-w-5xl w-full mx-auto px-6 py-12 flex-1">
        <div className="flex items-end justify-between mb-8 border-b border-border pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Monedero</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Créditos</h1>
          </div>
          <div className="flex items-center gap-3 border border-border rounded-sm px-5 py-3 bg-white" data-testid="credits-balance-box">
            <Coins className="w-6 h-6 text-[#CA8A04]" />
            <div>
              <div className="text-3xl font-display font-semibold tabular-nums leading-none">{user?.credits ?? 0}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">disponibles</div>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Comprar paquetes</p>
          <CreditPackages />
          <p className="text-xs text-neutral-400 mt-4">Pago seguro con Stripe · Tarjeta de prueba 4242 4242 4242 4242 · Compra 300 y recibe +30 de regalo</p>
        </div>

        <div className="mb-12 border border-border rounded-sm p-6 bg-gradient-to-br from-white to-[#002FA7]/5">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-[#002FA7]" />
            <p className="text-sm font-semibold">Invita y gana · +5 créditos para cada uno</p>
          </div>
          <p className="text-sm text-neutral-500 mb-4">Comparte tu enlace. Cuando alguien se registre con él, tú y esa persona recibís 5 créditos.</p>
          <div className="flex gap-2">
            <Input readOnly value={`${window.location.origin}/?ref=${user?.user_id || ""}`} className="rounded-sm text-sm" data-testid="referral-link" />
            <Button
              className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a] shrink-0"
              data-testid="copy-referral-btn"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${user?.user_id || ""}`); toast.success("Enlace copiado"); }}
            >
              <Copy className="w-4 h-4" /> Copiar
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-neutral-500" />
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">Historial de operaciones</p>
          </div>
          {busy ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" /></div>
          ) : history.length === 0 ? (
            <p className="text-neutral-500 py-8 text-center border border-dashed border-border rounded-sm">Aún no hay operaciones.</p>
          ) : (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="credit-history">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between px-4 py-3 border-b border-border/60 last:border-0 hover:bg-neutral-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${h.credits >= 0 ? "bg-green-50 text-green-600" : "bg-neutral-100 text-neutral-500"}`}>
                      {h.credits >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{ACTION_LABELS[h.action] || h.action}</div>
                      <div className="text-xs text-neutral-400">{fmt(h.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold tabular-nums ${h.credits >= 0 ? "text-green-600" : "text-neutral-700"}`}>
                      {h.credits >= 0 ? "+" : ""}{h.credits}
                    </div>
                    <div className="text-xs text-neutral-400 tabular-nums">saldo {h.balance_after}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
